import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { filterModes, MODE_CATEGORY_HEX, type ModeSummary, type PanelCounts } from '@/lib/modes';
import { AdminPage, RegistryTable, Pill, StatusPill, FeedbackControl } from '@/admin';

const REGISTRY = 'modes';

function PanelCountsCell({ counts }: { counts: PanelCounts }) {
  const req = counts.required ?? 0;
  const rec = counts.recommended ?? 0;
  const ssReq = counts.sex_specific_required ?? 0;
  const ssRec = counts.sex_specific_recommended ?? 0;
  if (!req && !rec && !ssReq && !ssRec) return <span className="text-text-muted">none authored</span>;
  return (
    <div className="text-[12px] leading-snug">
      <div><strong>{req} req</strong> / {rec} rec</div>
      {ssReq || ssRec ? <div className="text-text-muted">+{ssReq} / {ssRec} sex-specific</div> : null}
    </div>
  );
}

function ModifiersCell({ m }: { m: ModeSummary }) {
  if (!m.has_clinical_modifiers) return <StatusPill on={false} label="none" />;
  const parts: string[] = [];
  if (m.reference_range_overrides_count) parts.push(`${m.reference_range_overrides_count} ref ranges`);
  if (m.indicated_monitoring_count) parts.push(`${m.indicated_monitoring_count} monitoring`);
  if (m.contraindication_profile_count) parts.push(`${m.contraindication_profile_count} contra`);
  if (m.dose_modifications_count) parts.push(`${m.dose_modifications_count} dose mods`);
  if (!parts.length) return <StatusPill on label="configured" />;
  return (
    <div className="font-mono text-[11px] leading-snug text-text-secondary">
      {parts.map((p) => <div key={p}>{p}</div>)}
    </div>
  );
}

export function ModesPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['modes'], queryFn: () => adminApi.modes.list() });
  const fb = useQuery({ queryKey: ['feedback-summary', REGISTRY], queryFn: () => feedbackApi.summary(REGISTRY) });
  const [search, setSearch] = useState('');

  const modes = q.data?.modes ?? [];
  const rows = filterModes(modes, search);
  const warnings = q.data?.warnings ?? [];
  const refreshFb = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', REGISTRY] });

  return (
    <AdminPage
      title="Mode Registry"
      description="Every clinical mode. Open a mode to drill into the registries it links — panels → biomarkers, indicated compounds, contraindications, sub-branches and tasks. Flag any entry for review."
    >
      {q.data ? (
        <div className="mb-13 text-body-sm text-text-secondary">
          Registry version <strong className="text-text-primary">v{String(q.data.version ?? '?')}</strong>
          {q.data.last_updated ? ` · last updated ${q.data.last_updated}` : ''}
        </div>
      ) : null}

      {warnings.length ? (
        <div className="mb-13 rounded border border-warning bg-warning-soft p-13 text-body-sm">
          <strong className="text-warning">{warnings.length} registry warning{warnings.length > 1 ? 's' : ''}</strong>
          <ul className="mt-1 list-disc pl-5">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
        </div>
      ) : null}

      <RegistryTable<ModeSummary>
        columns={[
          {
            key: 'mode', header: 'Mode', render: (m) => (
              <div>
                <Link to={`/admin/modes/${encodeURIComponent(m.mode_id)}`} className="font-semibold text-accent hover:underline">{m.display_name}</Link>
                <div className="font-mono text-[11px] text-text-muted">{m.mode_id}</div>
                {m.description ? <div className="mt-1 max-w-[420px] text-[12px] text-text-secondary">{m.description.length > 160 ? `${m.description.slice(0, 159)}…` : m.description}</div> : null}
              </div>
            ),
          },
          { key: 'category', header: 'Category', render: (m) => <Pill label={m.category} color={MODE_CATEGORY_HEX[m.category]} /> },
          { key: 'panels', header: 'Panels', render: (m) => <PanelCountsCell counts={m.panel_counts} /> },
          { key: 'modifiers', header: 'Clinical modifiers', render: (m) => <ModifiersCell m={m} /> },
          { key: 'compounds', header: 'Compounds', className: 'font-mono text-[12px]', render: (m) => m.indicated_compounds_count || 0 },
        ]}
        rows={rows}
        getRowKey={(m) => m.mode_id}
        count={modes.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={search ? `No modes match "${search}"` : 'No modes authored in the registry yet.'}
        search={{ value: search, onChange: setSearch, placeholder: 'Search by name, id, or description…' }}
        rowActions={(m) => (
          <div className="flex items-center gap-2">
            <FeedbackControl registry={REGISTRY} itemId={m.mode_id} itemLabel={m.display_name} openCount={fb.data?.[m.mode_id]?.open ?? 0} onChanged={refreshFb} />
            <Link to={`/admin/modes/${encodeURIComponent(m.mode_id)}`} className="rounded-sm border border-border-strong px-2 py-1 text-[12px] hover:bg-surface-sunken">Open →</Link>
          </div>
        )}
      />
    </AdminPage>
  );
}
